import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  x: number;
  y: number;
  visible: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({ content, x, y, visible }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x, y });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (visible && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const offset = 4;

      // Calculate smart positioning with edge detection
      let finalX = x + offset;
      let finalY = y + offset;

      // Check right edge
      if (finalX + tooltipRect.width > window.innerWidth) {
        finalX = x - offset - tooltipRect.width;
      }

      // Check bottom edge
      if (finalY + tooltipRect.height > window.innerHeight) {
        finalY = y - offset - tooltipRect.height;
      }

      // Ensure tooltip stays within viewport (edge case for corners)
      finalX = Math.max(8, Math.min(finalX, window.innerWidth - tooltipRect.width - 8));
      finalY = Math.max(8, Math.min(finalY, window.innerHeight - tooltipRect.height - 8));

      setPosition({ x: finalX, y: finalY });
      setIsReady(true);
    } else {
      setIsReady(false);
    }
  }, [x, y, visible, content]);

  if (!visible) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={`fixed pointer-events-none transition-opacity duration-300 z-[9999] ${
        isReady ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      aria-hidden="true"
    >
      <div className="border border-zinc-100 bg-zinc-600 text-zinc-300 text-sm rounded px-2 py-1 shadow-lg max-w-[300px] break-words">
        {content}
      </div>
    </div>,
    document.body
  );
};
